import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Paginación del lado del cliente. Recibe la lista completa (ya filtrada) y
// devuelve la porción visible + controles. No requiere cambios de backend.
// options.persist: guarda/lee la página actual en la URL (?page=N) para
// restaurarla al regresar desde el detalle de un documento.
export function usePagination(items, initialSize = 50, options = {}) {
  const { persist = false } = options;
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPage = parseInt(searchParams.get("page"), 10);
  const [page, setPageState] = useState(persist && urlPage > 0 ? urlPage : 1);
  const [pageSize, setPageSize] = useState(initialSize);
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const setPage = (updater) => {
    setPageState((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      if (persist) {
        setSearchParams((sp) => {
          const nsp = new URLSearchParams(sp);
          if (next && next > 1) nsp.set("page", String(next));
          else nsp.delete("page");
          return nsp;
        }, { replace: true });
      }
      return next;
    });
  };

  useEffect(() => {
    // Solo corrige si ya hay datos cargados (evita perder la página restaurada
    // desde la URL mientras la lista aún está vacía).
    if (total > 0 && page > totalPages) setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPages, page, total]);

  const pageItems = useMemo(
    () => items.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize),
    [items, page, pageSize]
  );

  return { pageItems, page, setPage, pageSize, setPageSize, total, totalPages };
}

export function Pagination({ page, setPage, pageSize, setPageSize, total, totalPages, testid = "pagination" }) {
  if (!total) return null;
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(total, page * pageSize);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 mt-4" data-testid={testid}>
      <div className="font-mono-data text-xs text-[#5B6672]" data-testid={`${testid}-info`}>
        Mostrando {start}–{end} de {total}
      </div>
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 font-mono-data text-xs text-[#5B6672]">
          Por página
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            data-testid={`${testid}-size`}
            className="bg-white border border-[#E3E8EE] px-2 py-1 text-[#16202B] focus:outline-none focus:ring-1 focus:ring-[#465B7F]"
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </label>
        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            data-testid={`${testid}-prev`}
            className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#E3E8EE] font-mono-data text-xs uppercase tracking-wider text-[#16202B] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#E9EEF3] transition-colors"
          >
            <ChevronLeft size={14} /> Anterior
          </button>
          <span className="font-mono-data text-xs text-[#5B6672] px-2" data-testid={`${testid}-page`}>
            Pág. {page} / {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            data-testid={`${testid}-next`}
            className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#E3E8EE] font-mono-data text-xs uppercase tracking-wider text-[#16202B] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#E9EEF3] transition-colors"
          >
            Siguiente <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
